import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/auth";
import { useUserProfileRefresh } from "@/contexts/UserProfileRefreshContext";

export const useUserDropdown = () => {
  const { user } = useAuth();
  const { refreshTrigger } = useUserProfileRefresh();
  const [isAdmin, setIsAdmin] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [usernameSlug, setUsernameSlug] = useState<string | null>(null);
  const adminCheckComplete = useRef(false);
  const location = useLocation();

  const isOnDashboard = location.pathname.includes("/dashboard");
  const isOnProfilePage =
    location.pathname.includes("/profile/") ||
    location.pathname.includes("/u/");
  const isOnHomePage = location.pathname === "/";
  const isOnAdminPage = location.pathname.includes("/admin");
  const isOnSettingsPage = location.pathname.includes("/settings");
  const isOnNotificationsPage = location.pathname.includes("/notifications");
  const isOnSuperadminPage = location.pathname.includes("/superadmin");

  useEffect(() => {
    let isMounted = true;

    if (!user) {
      if (isMounted) {
        setIsAdmin(false);
        setUsername(null);
        setDisplayName(null);
        setAvatarUrl(null);
        setUsernameSlug(null);
        adminCheckComplete.current = false;
      }
      return;
    }

    const fetchUserProfile = async () => {
      if (user) {
        try {
          console.log("[useUserDropdown] Fetching user profile for auth user:", user.id);

          const { data: profileData, error } = await supabase
            .from("profiles")
            .select(
              "is_admin, username, avatar_url, username_slug, display_name"
            )
            .eq("id", user.id)
            .single();

          if (!error && profileData) {
            console.log("[useUserDropdown] Profile data fetched:", profileData);
            if (isMounted) {
              setIsAdmin(!!profileData.is_admin);
              setUsername(profileData.username);
              setDisplayName(profileData.display_name || profileData.username);
              setUsernameSlug(profileData.username_slug);

              if (
                profileData.avatar_url &&
                typeof profileData.avatar_url === "string" &&
                profileData.avatar_url.trim() !== ""
              ) {
                console.log(
                  "[useUserDropdown] Avatar URL from database:",
                  profileData.avatar_url
                );
                setAvatarUrl(profileData.avatar_url);
              } else {
                console.log("[useUserDropdown] No valid avatar URL found in profile");
                setAvatarUrl(null);
              }
              adminCheckComplete.current = true;
            }
          } else if (error) {
            console.error("[useUserDropdown] Error fetching profile:", error);
            if (isMounted) {
              setAvatarUrl(null);
            }
          }
        } catch (err) {
          console.error("[useUserDropdown] Error fetching user profile:", err);
          if (isMounted) {
            setAvatarUrl(null);
          }
        }
      }
    };

    // Reset adminCheckComplete when refreshTrigger changes to force refetch
    if (refreshTrigger > 0) {
      console.log("[useUserDropdown] Refresh triggered, resetting cache");
      adminCheckComplete.current = false;
    }

    fetchUserProfile();

    return () => {
      isMounted = false;
    };
  }, [user, refreshTrigger]);

  const getInitials = () => {
    if (displayName) {
      return displayName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    } else if (username) {
      return username
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    } else if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  return {
    user,
    isAdmin,
    username,
    displayName,
    avatarUrl,
    usernameSlug,
    getInitials,
    isOnDashboard,
    isOnProfilePage,
    isOnHomePage,
    isOnAdminPage,
    isOnSettingsPage,
    isOnNotificationsPage,
    isOnSuperadminPage,
  };
};
